import { create } from 'zustand';
import { fetchGetGeoProvinces } from '@/service/api/geo';

interface GeoState {
  /** 省份及下辖城市数据列表 */
  provinces: Api.Geo.GeoProvinceResponse[];
  /** 是否正在加载中 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;

  /** 获取省市二级数据列表 (自带缓存与并发去重) */
  fetchProvinces: () => Promise<Api.Geo.GeoProvinceResponse[]>;
  /** 根据 ID 获取省份对象 */
  getProvinceById: (id?: number | string) => Api.Geo.GeoProvinceResponse | undefined;
  /** 根据 ID 获取城市对象 */
  getCityById: (id?: number | string) => Api.Geo.GeoCityResponse | undefined;
  /** 根据 ID 获取城市名称 */
  getCityNameById: (id?: number | string, fallback?: string) => string;
  /** 清除错误信息 */
  clearError: () => void;
}

let fetchProvincesPromise: Promise<Api.Geo.GeoProvinceResponse[]> | null = null;

export const useGeoStore = create<GeoState>((set, get) => ({
  provinces: [],
  loading: false,
  error: null,

  fetchProvinces: async () => {
    const { provinces } = get();
    if (provinces.length > 0) {
      return provinces;
    }
    if (fetchProvincesPromise) {
      return fetchProvincesPromise;
    }

    set({ loading: true, error: null });
    fetchProvincesPromise = fetchGetGeoProvinces()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        set({ provinces: list, loading: false, error: null });
        return list;
      })
      .catch((err) => {
        const errorMessage = err instanceof Error ? err.message : '获取省市数据失败';
        set({ loading: false, error: errorMessage });
        throw err;
      })
      .finally(() => {
        fetchProvincesPromise = null;
      });

    return fetchProvincesPromise;
  },

  getProvinceById: (id) => {
    if (id === undefined || id === null || id === '') return undefined;
    const numId = Number(id);
    const { provinces } = get();
    return provinces.find((item) => item.id === numId || String(item.id) === String(id));
  },

  getCityById: (id) => {
    if (id === undefined || id === null || id === '') return undefined;
    const numId = Number(id);
    const { provinces } = get();
    for (const province of provinces) {
      if (province.children && province.children.length > 0) {
        const city = province.children.find(
          (c) => c.id === numId || String(c.id) === String(id)
        );
        if (city) return city;
      }
    }
    return undefined;
  },

  getCityNameById: (id, fallback = '') => {
    if (id === undefined || id === null || id === '') return fallback;
    const city = get().getCityById(id);
    return city?.city_name || fallback || String(id);
  },

  clearError: () => set({ error: null })
}));
